import React, { useRef, useState } from "react";
import { DraftingCompass } from "lucide-react";
import { useIntl } from "react-intl";
import Popover from "../../../../../../library/UI/Popover";
import ChartTabs from "../ChartTabs";
import DrawingToolList from "./DrawingToolList";
import CurrentDrawings from "./CurrentDrawings";
import useFitChartMenu from "../../hooks/useFitChartMenu";
import "./DrawingsMenu.sass";

const TRIGGER_ICON_SIZE = 18;
const TAB_CURRENT = "current";
const TAB_ALL = "all";

/**
 * Drawings toolbar button with the count badge and its menu
 * (po-chart-visual-spec.md §7.1): the tools; with drawings on the chart, the
 * Current / All tabs and «Delete all (N)», like the indicators menu. A picked
 * tool puts its drawing on the chart at once and the menu stays open (§7.5).
 *
 * @param {object}   props
 * @param {object[]} props.drawings - engine.getDrawings() items (useDrawings)
 * @param {(toolId: string) => void} props.onAddTool
 * @param {(id: string) => void} props.onEdit
 * @param {(id: string) => void} props.onRemove
 * @param {() => void} props.onRemoveAll
 * @param {boolean}  props.isOpen
 * @param {() => void} props.onToggle
 * @param {() => void} props.onClose
 */
const DrawingsMenu = ({
  drawings,
  onAddTool,
  onEdit,
  onRemove,
  onRemoveAll,
  isOpen,
  onToggle,
  onClose,
}) => {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const [tab, setTab] = useState(TAB_ALL);
  const menuRef = useRef(null);
  const menuStyle = useFitChartMenu(menuRef, isOpen);

  const count = drawings.length;
  // Without drawings there is only the list of tools; the last one removed falls back to it
  const activeTab = count > 0 ? tab : TAB_ALL;
  const label = t("futures.chart.toolbar.drawings");
  const tabs = [
    { id: TAB_CURRENT, label: t("futures.chart.drawings.tab_current") },
    { id: TAB_ALL, label: t("futures.chart.drawings.tab_all") },
  ];

  const trigger = (
    <button
      type="button"
      className={`chart-toolbar__btn${isOpen ? " chart-toolbar__btn--active" : ""}`}
      aria-label={label}
      title={label}
      aria-expanded={isOpen}
      onClick={onToggle}
    >
      <span className="chart-toolbar__btn-icon">
        <DraftingCompass size={TRIGGER_ICON_SIZE} aria-hidden="true" />
      </span>
      {count > 0 && (
        <span className="chart-toolbar__badge" aria-hidden="true">
          {count}
        </span>
      )}
    </button>
  );

  return (
    <Popover isOpen={isOpen} onClose={onClose} trigger={trigger}>
      <div className="drawings-menu" ref={menuRef} style={menuStyle}>
        {count > 0 && (
          <div className="drawings-menu__tabs">
            <ChartTabs tabs={tabs} activeId={activeTab} onSelect={setTab} />
          </div>
        )}
        <div className="drawings-menu__list">
          {activeTab === TAB_CURRENT ? (
            <CurrentDrawings drawings={drawings} onEdit={onEdit} onRemove={onRemove} />
          ) : (
            <DrawingToolList onSelect={onAddTool} />
          )}
        </div>
        {count > 0 && (
          <button type="button" className="drawings-menu__delete-all" onClick={onRemoveAll}>
            {t("futures.chart.drawings.delete_all", { count })}
          </button>
        )}
      </div>
    </Popover>
  );
};

export default DrawingsMenu;
